import React, { useEffect } from 'react';
import type { ComponentProps } from 'react';
import styled from '@emotion/styled';
import type { BackgroundColorProps, OverflowProps } from 'styled-system';
import {
  backgroundColor,
  height,
  overflow,
  position as positionStyle,
  space,
  width,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import { LazyImage, ResponsiveLazyImage } from 'common/components/LazyImage';
import { CONTENT_PRODUCT_CARD_IMG_SIZES } from 'common/components/ProductCard/constants/constants';
import { usePromoEvents } from 'common/hooks/dynamic-yield';
import { focusOutlineCSS } from 'common/theme/styles';
import type { AspectRatioProps } from 'common/theme/system-utilities';
import { aspectRatio, textDecoration } from 'common/theme/system-utilities';
import { LIST_VIEW_TEASER_POSITION } from '../../product-list-view/components/constants';
import ListViewTeaserContent from './ListViewTeaserContent';
import { TeaserInfoLabel } from './TeaserInfoLabel';
import type { ListViewTeaserProps } from './types';

const ListViewTeaserContainer = (props: ComponentProps<typeof Flex>) => (
  <Flex
    data-testid="list-view-teaser-container"
    flexDirection="column"
    mx="auto"
    pb={['sp_24', 'sp_32', 'sp_48']}
    width={CONTENT_PRODUCT_CARD_IMG_SIZES}
    {...props}
  />
);

const UnstyledListViewTeaserContainerRounded = styled(ListViewTeaserContainer)<any>`
  ${height};
  ${positionStyle};
`;

const ListViewTeaserContainerRounded = (
  props: ComponentProps<typeof UnstyledListViewTeaserContainerRounded>
) => (
  <UnstyledListViewTeaserContainerRounded
    height="100%"
    position="relative"
    pb="sp_0"
    width={1}
    {...props}
  />
);

const UnstyledListViewLink = styled(Flex)<any>`
  ${textDecoration};
  ${focusOutlineCSS};
`;

const listViewLinkStyles: ComponentProps<typeof UnstyledListViewLink> = {
  as: 'a',
  flexDirection: 'column',
  textDecoration: 'none',
};

const ListViewLink = (props: ComponentProps<typeof UnstyledListViewLink>) => (
  <UnstyledListViewLink {...listViewLinkStyles} {...props} />
);

const UnstyledListViewLinkRounded = styled(Flex)<any>`
  ${textDecoration};
  ${focusOutlineCSS};
`;

const ListViewLinkRounded = (props: ComponentProps<typeof UnstyledListViewLinkRounded>) => {
  return (
    <UnstyledListViewLinkRounded
      {...listViewLinkStyles}
      height="100%"
      pb="sp_0"
      width={1}
      borderRadius="medium"
      overflow="hidden"
      {...props}
    />
  );
};

const UnstyledTeaserPicture = styled(ResponsiveLazyImage)<any>`
  ${height};
  ${width};
`;

const TeaserPicture = (props: ComponentProps<typeof UnstyledTeaserPicture>) => (
  <UnstyledTeaserPicture width={CONTENT_PRODUCT_CARD_IMG_SIZES} {...props} />
);
const UnstyledTeaserPictureRounded = styled(LazyImage)<
  OverflowProps & AspectRatioProps & BackgroundColorProps
>`
  ${space};
  ${width};
  ${overflow};
  ${aspectRatio};
  ${backgroundColor};
`;

const TeaserPictureRounded = (props: ComponentProps<typeof UnstyledTeaserPictureRounded>) => (
  <UnstyledTeaserPictureRounded
    width={1}
    overflow="initial"
    backgroundColor="sys.neutral.background.default"
    aspectRatio="1 / 1"
    {...props}
  />
);

const ListViewTeaser = ({
  picture,
  ctaLink: { target, fullUrl },
  ctaButtonAriaLabel,
  tracking,
  isRounded,
  legalDisclaimerContent,
  legalDisclaimerLabel,
  textColor,
  headlineColor = textColor,
  sublineColor = textColor,
  ...props
}: ListViewTeaserProps) => {
  const { onPromoView, onPromoClick } = usePromoEvents({
    ...tracking,
    constructorAdId: tracking.constructorBannerId,
    position: LIST_VIEW_TEASER_POSITION,
  });

  useEffect(() => onPromoView(), []);

  const ViewLink = isRounded ? ListViewLinkRounded : ListViewLink;
  const TeaserContainer = isRounded ? ListViewTeaserContainerRounded : ListViewTeaserContainer;

  return (
    <TeaserContainer
      data-corgi-id="list-view-teaser"
      {...(tracking.constructorBannerId && {
        'data-cnstrc-media-banner-id': tracking.constructorBannerId,
      })}
      {...(tracking.constructorPlacementId && {
        'data-cnstrc-media-placement-id': tracking.constructorPlacementId,
      })}
      {...(isRounded && {
        alignItems: 'space-between',
      })}
    >
      <ViewLink
        href={fullUrl}
        target={target}
        onClick={onPromoClick}
        {...(ctaButtonAriaLabel && { ['aria-label']: ctaButtonAriaLabel })}
      >
        <Flex position="relative" width={1}>
          {isRounded ? (
            <TeaserPictureRounded
              data-testid="rounded-teaser-picture"
              alt={picture.alt}
              thumbnail={picture.thumbnail}
              preloadImage={picture.preloadImage}
              {...picture.large_desktop}
            />
          ) : (
            <TeaserPicture data-testid="teaser-picture" {...picture} />
          )}
          {legalDisclaimerLabel && legalDisclaimerContent && (
            <TeaserInfoLabel
              badgeText={legalDisclaimerLabel}
              drawerContent={legalDisclaimerContent}
              position="absolute"
              themeBottom={['sp_4', null, null, 'sp_8']}
              themeLeft={['sp_4', null, null, 'sp_8']}
            />
          )}
        </Flex>
        <ListViewTeaserContent
          {...props}
          isRounded={isRounded}
          textColor={textColor}
          sublineColor={sublineColor}
          headlineColor={headlineColor}
        />
      </ViewLink>
    </TeaserContainer>
  );
};

export default ListViewTeaser;
